<script setup lang="ts">
import { inject } from 'vue';
import Carousel from 'primevue/carousel';
import { CarouselImage } from '@/modules/content-blocks/interfaces/carousel.interface';
import { createDefaultImage } from '@/modules/content-blocks/helpers/carousel.helper';

const props = defineProps({
   data: {
      type: Object,
      default: () => ({}),
   },
});
const viewport = inject<ComputedRef<'desktop' | 'tablet' | 'mobile'>>('viewport');

const images = computed<CarouselImage[]>(() => props.data.slides as CarouselImage[]);

// Fill images to always have at least 3 (MIN_VISIBLE_IMAGES) items, on desktop and tablet
const MIN_VISIBLE_IMAGES = 3;
const MOBILE_VISIBLE_IMAGES = 1;
const MOBILE_BREAKPOINT = 400;

const isMobileWidth = ref(false);

const checkWidth = () => {
   isMobileWidth.value = window.innerWidth <= MOBILE_BREAKPOINT;
};

onMounted(() => {
   checkWidth();
   window.addEventListener('resize', checkWidth);
});

onUnmounted(() => {
   window.removeEventListener('resize', checkWidth);
});

const numVisible = computed(() => {
   const currentViewport = viewport?.value;
   return currentViewport === 'mobile' || isMobileWidth.value ? MOBILE_VISIBLE_IMAGES : MIN_VISIBLE_IMAGES;
});

const filledImages = computed<CarouselImage[]>(() => {
   const base = images.value ? [...images.value] : [];
   const needed = MIN_VISIBLE_IMAGES - base.length;
   for (let i = 0; i < needed; i++) {
      base.push(createDefaultImage());
   }
   return base;
});
</script>

<template>
   <section v-if="images?.length" :class="['carousel-content', { 'carousel-content--mobile': viewport === 'mobile' || isMobileWidth }]">
      <Carousel :key="`carousel-${viewport}-${isMobileWidth}`" :value="filledImages" :numVisible="numVisible" :numScroll="1">
         <template #item="slotProps">
            <div class="carousel-content__item">
               <img v-if="slotProps.data.image.url" :src="slotProps.data.image.url" />
            </div>
         </template>
      </Carousel>
   </section>
   <div v-else>{{ $tb('live-editor.content-blocks.carousel.no-images-configured') }}</div>
</template>

<style scoped lang="scss">
.carousel-content {
   position: relative;
   width: 100%;
   max-width: 1000px;
   margin: 0 auto;

   &__item {
      height: 100%;
      display: flex;
      margin: 0 var(--nxt-gutter);

      img {
         width: 100%;
         height: 100%;
         border-radius: var(--jobsite-border-radius, var(--nxt-radius));
         object-fit: cover;
         display: block;
      }
   }

   /*
    * primevue carousel button overrides
    */
   :deep(button.p-disabled) {
      color: transparent;
   }
}
</style>
